/**
 * Outbox queue for offline actions (IndexedDB).
 */

import { offlineDb, OUTBOX_STATUS } from './db.js';
import { MAX_SYNC_ATTEMPTS } from './offlineConfig.js';

const DEDUP_STATUSES = [
  OUTBOX_STATUS.PENDING,
  OUTBOX_STATUS.FAILED,
];

/**
 * F-35 — la file appartient à l'utilisateur qui l'a remplie. Identifiant de
 * l'utilisateur connecté (localStorage 'user', posé par auth.service), ou null.
 */
export function currentOutboxUserId() {
  try {
    const raw = localStorage.getItem('user');
    const id = raw ? JSON.parse(raw)?.id : null;
    return id === undefined || id === null || id === '' ? null : String(id);
  } catch {
    return null;
  }
}

/** Ligne mise en file avant la v3 du schéma : pas de propriétaire connu. */
export function isLegacyOwnerlessRow(row) {
  return row?.ownerUserId === undefined || row?.ownerUserId === null;
}

// Une ligne est « à » l'utilisateur s'il l'a mise en file. Les lignes héritées
// (sans propriétaire) restent visibles de tous : le serveur tranche au rejeu
// (voir syncEngine — un 403 les garde en attente sans consommer d'essai).
// Sans utilisateur (ownerUserId null), seules les lignes héritées sont visibles.
function belongsTo(row, ownerUserId) {
  if (isLegacyOwnerlessRow(row)) return true;
  return ownerUserId !== null && String(row.ownerUserId) === String(ownerUserId);
}

export async function enqueueOutboxAction({
  type,
  beneficiaireId,
  payload,
  idempotencyKey,
  blobOutboxId = null,
}) {
  const ownerUserId = currentOutboxUserId();
  return offlineDb.transaction('rw', offlineDb.outbox, async () => {
    // Dédoublonnage limité au même propriétaire : l'action d'un autre compte
    // sur cet appareil n'est jamais écrasée ni reprise à son compte.
    const existing = await offlineDb.outbox
      .where('idempotencyKey')
      .equals(idempotencyKey)
      .filter((row) => DEDUP_STATUSES.includes(row.status)
        && !isLegacyOwnerlessRow(row)
        && String(row.ownerUserId) === String(ownerUserId))
      .first();
    if (existing) {
      await offlineDb.outbox.update(existing.id, {
        payload,
        blobOutboxId,
        status: OUTBOX_STATUS.PENDING,
        updatedAt: new Date().toISOString(),
      });
      return existing.id;
    }
    return offlineDb.outbox.add({
      type,
      beneficiaireId,
      payload,
      idempotencyKey,
      blobOutboxId,
      ownerUserId,
      status: OUTBOX_STATUS.PENDING,
      attempts: 0,
      errorMessage: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  });
}

// `ownerUserId` : par défaut l'utilisateur connecté ; null = aucun (seules les
// lignes héritées sans propriétaire sont alors renvoyées).
export async function listPendingOutbox(beneficiaireId = null, ownerUserId = currentOutboxUserId()) {
  let query = offlineDb.outbox.where('status').anyOf([
    OUTBOX_STATUS.PENDING,
    OUTBOX_STATUS.FAILED,
  ]);
  const rows = (await query.toArray()).filter((r) => belongsTo(r, ownerUserId));
  const filtered = beneficiaireId
    ? rows.filter((r) => r.beneficiaireId === beneficiaireId)
    : rows;
  return filtered.sort((a, b) => a.id - b.id);
}

export async function countPendingOutbox(ownerUserId = currentOutboxUserId()) {
  return offlineDb.outbox
    .where('status')
    .anyOf([OUTBOX_STATUS.PENDING, OUTBOX_STATUS.FAILED])
    .filter((row) => belongsTo(row, ownerUserId))
    .count();
}

// Une ligne ERROR porte un `errorCode` quand le serveur a REFUSÉ l'action de
// façon définitive (F-07 : demande annulée, coupe-circuit fonds, parcours
// verrouillé…). La retenter ne changerait rien : elle n'est ni comptée parmi les
// « échecs » génériques, ni remise en file par « Réessayer » — elle est montrée
// une par une (libellé + motif) jusqu'à ce que l'utilisateur en prenne acte.
function isRefusedRow(row) {
  return Boolean(row?.errorCode);
}

// Terminal failures (gave up after MAX_SYNC_ATTEMPTS) — surfaced separately so
// the user can act, without them blocking the pending count / idle timeout.
// Les refus définitifs du serveur sont exclus (voir listRefusedOutbox).
export async function countErrorOutbox(ownerUserId = currentOutboxUserId()) {
  return offlineDb.outbox
    .where('status')
    .equals(OUTBOX_STATUS.ERROR)
    .filter((row) => !isRefusedRow(row) && belongsTo(row, ownerUserId))
    .count();
}

// Refus définitifs du serveur, du plus ancien au plus récent.
export async function listRefusedOutbox(ownerUserId = currentOutboxUserId()) {
  const rows = await offlineDb.outbox
    .where('status')
    .equals(OUTBOX_STATUS.ERROR)
    .filter((row) => isRefusedRow(row) && belongsTo(row, ownerUserId))
    .toArray();
  return rows.sort((a, b) => a.id - b.id);
}

// « Compris » : l'utilisateur a vu le refus — la ligne (et sa photo éventuelle)
// quitte l'appareil. Ne touche qu'aux refus, jamais à une action encore due.
export async function dismissRefusedOutbox(id) {
  const row = await offlineDb.outbox.get(id);
  if (!row || row.status !== OUTBOX_STATUS.ERROR || !isRefusedRow(row)) return false;
  await deleteOutboxAction(id);
  return true;
}

// Recover actions orphaned in SYNCING by an interrupted flush (crash, tab close,
// or a 401 page-reload mid-sync). Safe because flushes are single-flight: at
// (re)start no action is legitimately mid-flight, so any SYNCING is a leftover.
export async function recoverStuckSyncing() {
  const stuck = await offlineDb.outbox
    .where('status')
    .equals(OUTBOX_STATUS.SYNCING)
    .toArray();
  await Promise.all(
    stuck.map((row) =>
      offlineDb.outbox.update(row.id, {
        status: OUTBOX_STATUS.PENDING,
        updatedAt: new Date().toISOString(),
      }),
    ),
  );
  return stuck.length;
}

// Manual recourse for terminal (ERROR) actions: put them back in the queue with
// a fresh attempt budget so the user can retry once the blocker is resolved.
// Un refus définitif du serveur (errorCode) n'est PAS remis en file : le
// rejouer serait refusé de nouveau, indéfiniment (F-07).
export async function resetErroredToPending(ownerUserId = currentOutboxUserId()) {
  const errored = await offlineDb.outbox
    .where('status')
    .equals(OUTBOX_STATUS.ERROR)
    .filter((row) => !isRefusedRow(row) && belongsTo(row, ownerUserId))
    .toArray();
  await Promise.all(
    errored.map((row) =>
      offlineDb.outbox.update(row.id, {
        status: OUTBOX_STATUS.PENDING,
        attempts: 0,
        errorMessage: null,
        updatedAt: new Date().toISOString(),
      }),
    ),
  );
  return errored.length;
}

export async function markOutboxSyncing(id) {
  await offlineDb.outbox.update(id, {
    status: OUTBOX_STATUS.SYNCING,
    updatedAt: new Date().toISOString(),
  });
}

export async function markOutboxSynced(id) {
  // Delete outbox row before blob so a resurrected SYNCING row never points at a
  // blob that was already pruned (recoverStuckSyncing).
  await offlineDb.transaction('rw', [offlineDb.outbox, offlineDb.proof_blobs], async () => {
    await offlineDb.outbox.delete(id);
    await offlineDb.proof_blobs.delete(id);
  });
}

export async function deleteOutboxAction(id) {
  await offlineDb.transaction('rw', [offlineDb.outbox, offlineDb.proof_blobs], async () => {
    await offlineDb.outbox.delete(id);
    await offlineDb.proof_blobs.delete(id);
  });
}

// Remise en attente SANS consommer de tentative : session absente (401, F-29)
// ou ligne héritée refusée à un autre compte (403, F-35). L'action attend son
// propriétaire / la reconnexion au lieu de s'épuiser en ERROR.
export async function markOutboxHeld(id) {
  await offlineDb.outbox.update(id, {
    status: OUTBOX_STATUS.PENDING,
    updatedAt: new Date().toISOString(),
  });
}

export async function markOutboxFailed(id, errorMessage) {
  const row = await offlineDb.outbox.get(id);
  const attempts = (row?.attempts || 0) + 1;
  const terminal = attempts >= MAX_SYNC_ATTEMPTS;
  await offlineDb.outbox.update(id, {
    status: terminal ? OUTBOX_STATUS.ERROR : OUTBOX_STATUS.FAILED,
    attempts,
    errorMessage: errorMessage || 'Erreur de synchronisation',
    updatedAt: new Date().toISOString(),
  });
  return { terminal, attempts };
}

// Refus définitif du serveur (F-07) : terminal dès la première tentative, avec
// le code d'erreur qui le distingue d'un épuisement des retentatives.
export async function markOutboxTerminal(id, errorMessage, errorCode) {
  const row = await offlineDb.outbox.get(id);
  const attempts = (row?.attempts || 0) + 1;
  await offlineDb.outbox.update(id, {
    status: OUTBOX_STATUS.ERROR,
    attempts,
    errorMessage: errorMessage || 'Action refusée par le serveur',
    errorCode: errorCode || 'REFUSED',
    updatedAt: new Date().toISOString(),
  });
  return { terminal: true, attempts };
}

export async function storeProofBlob(outboxId, blob, meta = {}) {
  await offlineDb.proof_blobs.put({
    outboxId,
    blob,
    mimeType: blob?.type || 'image/jpeg',
    fileName: meta.fileName || `preuve-${outboxId}.jpg`,
    createdAt: new Date().toISOString(),
  });
}

export async function getProofBlob(outboxId) {
  return offlineDb.proof_blobs.get(outboxId);
}

export async function deleteProofBlob(outboxId) {
  await offlineDb.proof_blobs.delete(outboxId);
}
